import { useMutation } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import { Comment, Settings, ThumbUp } from '@material-ui/icons';
import { ThumbDown } from '@material-ui/icons';
import { startCase } from 'lodash';
import React from 'react';

import { jdUpdateContextDescription } from 'shared/models/jd-llm-updates';
import { hasRole } from 'shared/models/user';
import { JDUpdateResultSchema } from 'tensorzero';

import { downloadFile, getInferenceData } from '../api';
import { PageDialogLink } from '../common/page-dialog-link';
import { fullDateTime } from '../common/timestamp';
import { JobEdit } from '../containers/job-edit';
import { JDLLMUpdate, JOB_UNDO_LLM_UPDATE } from '../graphql/queries/job-form';
import { useModal } from '../hooks/use-modal';
import { usePromptInfo } from '../hooks/use-prompt-info';
import { useSession } from '../hooks/use-session';
import { useSnackbar } from '../hooks/use-snackbar';
import { JDDiff } from './jd-diffs';

const detailsDialogStyles = css`
    .MuiDialogTitle-root {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;

const rawResultsStyle = css`
    display: flex;
    flex-direction: column;

    .MuiInputBase-inputMultiline {
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background-color: white;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.3);
        }
    }

    .MuiTextField-root {
        margin-bottom: 40px;
    }
`;

const textFieldRows = 10;

export const JDLLMUpdateDetails: React.FC<{
    open: boolean;
    onClose: () => void;
    job: { id: string; title: string; client: { name: string }; assignee: string; accountManagerId: string };
    updateData: JDLLMUpdate;
    onCreateRating?: (rating: boolean, comment?: string) => void;
}> = ({ open, onClose, job, updateData, onCreateRating }) => {
    const [undoUpdate] = useMutation<{}, { updateId: number }>(JOB_UNDO_LLM_UPDATE);
    const session = useSession();
    const { setAlert, showLoading, hideLoading } = useModal();
    const { setSnackbar } = useSnackbar();
    const { showPromptInfo } = usePromptInfo();
    const [comment, setComment] = React.useState('');
    const [commentDialogOpen, setCommentDialogOpen] = React.useState(false);
    const [undoDialogOpen, setUndoDialogOpen] = React.useState(false);
    const canUndoUpdate =
        session.user.id === job.assignee ||
        session.user.id === job.accountManagerId ||
        hasRole(session.userPermissions, 'prompts_editor');

    const handleViewInput = async () => {
        try {
            showLoading();
            const response = await downloadFile(updateData.input);
            const input = await response.json();
            setAlert('JD Changes Input', <TextField value={input} multiline={true} fullWidth={true} />, false, true);
            hideLoading();
        } catch {
            hideLoading();
            setAlert('Error', 'Failed to fetch input');
        }
    };

    const handleShowPrompts = () => {
        showPromptInfo({ resultsFile: updateData.resultsFile, inferenceId: updateData.inferenceId });
    };

    const handleUndoConfirmed = async () => {
        try {
            setUndoDialogOpen(false);
            showLoading();
            await undoUpdate({ variables: { updateId: updateData.id } });
            setSnackbar('Update reverted');
            onClose();
        } catch {
            setAlert('Error', 'Failed to revert update');
        } finally {
            hideLoading();
        }
    };

    const handleKeepChanges = () => {
        setUndoDialogOpen(false);
        setCommentDialogOpen(true);
    };

    const handleCreateRating = (rating: boolean) => async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (rating === false) {
            if (canUndoUpdate) {
                setUndoDialogOpen(true);
            } else {
                setCommentDialogOpen(true);
            }
        } else {
            onCreateRating?.(rating);
            onClose();
        }
    };

    const handleCreateRatingWithComment = () => {
        onCreateRating?.(false, comment);
        setCommentDialogOpen(false);
        setComment('');
        onClose();
    };

    const handleCancelComment = () => {
        setCommentDialogOpen(false);
        setComment('');
    };

    const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setComment(event.target.value);
    };

    const handleShowRawResults = async () => {
        try {
            showLoading();
            let responseContent: { parsedResponse: JDUpdateResultSchema };
            if (updateData.resultsFile) {
                const response = await downloadFile(updateData.resultsFile);
                responseContent = await response.json();
            } else if (updateData.inferenceId) {
                const inferenceData = await getInferenceData(updateData.inferenceId);
                responseContent = { parsedResponse: inferenceData.data.parsedResponse };
            } else {
                throw new Error('No results file or inferenceId');
            }
            hideLoading();
            const fields: Array<keyof JDUpdateResultSchema> = [
                'relevantInformation',
                'removedInformation',
                'addedInformation',
                'updatedInformation',
                'finalData'
            ];
            const textFields = fields.map((field) => {
                if (!responseContent.parsedResponse[field]) return null;
                const value =
                    typeof responseContent.parsedResponse[field] === 'object'
                        ? JSON.stringify(responseContent.parsedResponse[field], null, 2)
                        : responseContent.parsedResponse[field];
                const rows = field === 'finalData' ? textFieldRows * 2 : textFieldRows / 2;
                return responseContent.parsedResponse[field] ? (
                    <TextField
                        key={field}
                        value={value}
                        label={startCase(field)}
                        fullWidth={true}
                        multiline={true}
                        variant="outlined"
                        rows={rows}
                        InputProps={{ readOnly: true }}
                    />
                ) : null;
            });
            const info = (
                <div css={rawResultsStyle}>
                    {textFields}
                    <TextField
                        value={JSON.stringify(responseContent.parsedResponse, null, 2)}
                        label="Full Result"
                        fullWidth={true}
                        multiline={true}
                        variant="outlined"
                        rows={textFieldRows * 2}
                        InputProps={{ readOnly: true }}
                    />
                </div>
            );
            setAlert('JD Update - Raw LLM Result', info, false, true);
        } catch {
            hideLoading();
            setAlert('Error', 'Failed to fetch results');
        }
    };

    const viewPromptsButton = hasRole(session.userPermissions, 'prompts_editor') ? (
        <Button onClick={handleShowPrompts}>Prompts</Button>
    ) : null;
    const viewRawResultsButton = hasRole(session.userPermissions, 'prompts_editor') ? (
        <Button onClick={handleShowRawResults}>Raw Result</Button>
    ) : null;

    const actionButtons = onCreateRating ? (
        <>
            <Tooltip title="Poor Update">
                <Button onClick={handleCreateRating(false)}>
                    <ThumbDown fontSize="small" />
                </Button>
            </Tooltip>
            <Tooltip title="Good Update">
                <Button onClick={handleCreateRating(true)}>
                    <ThumbUp fontSize="small" />
                </Button>
            </Tooltip>
        </>
    ) : (
        <Button onClick={onClose}>Close</Button>
    );

    const commentDialog = (
        <Dialog open={commentDialogOpen} onClose={handleCancelComment} maxWidth="sm" fullWidth={true}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    Add Comment
                </Typography>
            </DialogTitle>
            <DialogContent>
                <TextField
                    value={comment}
                    label="Comment"
                    onChange={handleCommentChange}
                    multiline={true}
                    fullWidth={true}
                    placeholder="Any suggestions to make the update better (optional)?"
                    autoFocus={true}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelComment}>Cancel</Button>
                <Button onClick={handleCreateRatingWithComment}>{comment.length ? 'Submit' : 'Skip'}</Button>
            </DialogActions>
        </Dialog>
    );

    const undoDialog = (
        <Dialog open={undoDialogOpen} onClose={handleKeepChanges} maxWidth="sm" fullWidth={true}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    Poor Update
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" component="div">
                    Would you like to revert these changes?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleKeepChanges}>Keep Changes</Button>
                <Button onClick={handleUndoConfirmed}>Revert</Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Dialog open={open} onClose={onClose} css={detailsDialogStyles} fullWidth={true} maxWidth="md">
            <DialogTitle disableTypography={true}>
                <Typography variant="h4" component="div">
                    {job.client.name} - {job.title}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                    {jdUpdateContextDescription(updateData.context)} - {fullDateTime(updateData.updatedAt)}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <JDDiff updates={updateData.updates} />
            </DialogContent>
            <DialogActions>
                {viewPromptsButton}
                {viewRawResultsButton}
                <PageDialogLink Component={JobEdit} componentProps={{ id: job.id }} url={`/job/${job.id}/edit`}>
                    <Tooltip title="Job Settings">
                        <Button>
                            <Settings fontSize="small" />
                        </Button>
                    </Tooltip>
                </PageDialogLink>
                <Tooltip title="JD Changes Input">
                    <Button onClick={handleViewInput}>
                        <Comment fontSize="small" />
                    </Button>
                </Tooltip>
                {actionButtons}
            </DialogActions>
            {commentDialog}
            {undoDialog}
        </Dialog>
    );
};
